import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
/**
 * Card de Conteúdo do Botão de Ação.
 *
 * Edita duas coisas distintas:
 * 1. o botão primário (`text`/`url`/`size` na raiz do actionButton) — sempre
 *    visível, porque é o que o modo "sobre o vídeo" e os vídeos legados usam;
 * 2. o bloco de oferta (`actionButton.template`) — só no modo "abaixo do vídeo".
 *
 * Os campos do bloco saem do `layout` do modelo (`presets.ts`). Não existe
 * `if (model === ...)` aqui: quem decide o que aparece é a tabela.
 */

import React, { useId } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LayoutTemplate, MousePointerClick } from 'lucide-react';
import type {
  ActionButton,
  ActionButtonModel,
  ActionButtonTemplate,
  ActionButtonTextSize,
  ActionButtonToggleableText,
} from '@/store/videoFeatures';
import { hasSlot, modelSpec } from '@/lib/action-button';
import { ensureHttps } from '@/utils/url';
import { ActionButtonModelSelector } from './ActionButtonModelSelector';
import {
  TEXT_SLOT_LABELS,
  TOGGLEABLE_SLOTS,
  isTextSlot,
  secondarySectionLabel,
  type TextSlot,
} from './slot-labels';

interface ButtonContentCardProps {
  actionButton: Partial<ActionButton>;
  updateActionButton: (key: string, value: any) => void;
  /** Grava um campo do bloco de oferta: updateTemplate('headline', {...}) */
  updateTemplate: (key: string, value: any) => void;
  /** Troca o modelo inteiro de uma vez (já vem pronto do switchActionButtonModel). */
  onSelectModel: (model: ActionButtonModel) => void;
  /** false na aba "sobre o vídeo": esconde seletor e campos do bloco de oferta. */
  showTemplates?: boolean;
}

const TEXT_SIZE_OPTIONS: { value: ActionButtonTextSize; label: string }[] = [
  { value: 'small', get label() { return translateInterfaceText("Pequeno"); } },
  { value: 'medium', get label() { return translateInterfaceText("Médio"); } },
  { value: 'large', get label() { return translateInterfaceText("Grande"); } },
  { value: 'extra-large', get label() { return translateInterfaceText("Extra grande"); } },
];

const TEXT_SLOT_PLACEHOLDERS: Record<TextSlot, string> = {
  urgency: '⚠️ ÚLTIMA CHANCE!',
  headline: 'Aproveite essa condição especial',
  subheadline: 'Disponível somente nesta página',
  offerText: 'Adicione essa oferta ao seu pedido agora por apenas:',
  strikethrough: 'Valor total: R$ 999',
  featured: 'Seu preço hoje: R$ 99',
  price: '12x de R$ 12,89',
  priceNote: 'ou R$ 154,68 à vista',
};

/** Divisor com título, no mesmo padrão das seções do card de Estilo. */
const SectionDivider: React.FC<{ label: string }> = ({ label }) => { useLanguage(); return ((
  <div className="flex items-center gap-2">
    <div className="h-px flex-1 bg-border" />
    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    <div className="h-px flex-1 bg-border" />
  </div>
)); };

export const ButtonContentCard: React.FC<ButtonContentCardProps> = ({
  actionButton,
  updateActionButton,
  updateTemplate,
  onSelectModel,
  showTemplates = true,
}) => {
  const openNewTabId = useId();
  const secondaryOpenNewTabId = useId();
  const template: ActionButtonTemplate = actionButton.template || {};
  const model: ActionButtonModel = template.model || 'default';
  const spec = modelSpec(model);
  const progressBar = template.progressBar || {};
  const secondary = template.secondary || {};

  // O modelo diz SE existe segundo caminho (`secondaryAs: null` = não existe);
  // a escolha do usuário em `renderAs` diz a FORMA. Mesma resolução no card de
  // Estilo e no render — os três precisam concordar.
  const secondaryAs = spec.secondaryAs ? secondary.renderAs || spec.secondaryAs : null;

  /** Mescla um patch dentro de um objeto do template, preservando o resto. */
  const patchTemplate = (key: keyof ActionButtonTemplate, patch: Record<string, unknown>) => {
    const current = (template[key] ?? {}) as Record<string, unknown>;
    updateTemplate(key, { ...current, ...patch });
  };

  /** Slots textuais do modelo, na ordem do layout. */
  const textSlots = showTemplates ? spec.layout.filter(isTextSlot) : [];

  return (
    <div className="space-y-5">
      {showTemplates && (
        <Card className="border-border bg-card shadow-sm">
          <CardContent className="space-y-5 p-6">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Modelo do bloco")}</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                {translateInterfaceText("Escolha o arranjo do bloco de oferta que aparece abaixo do vídeo.")}</p>
            </div>

            <ActionButtonModelSelector selectedModel={model} onSelect={onSelectModel} />
          </CardContent>
        </Card>
      )}

      <Card className="border-border bg-card shadow-sm">
        <CardContent className="space-y-5 p-6">
          <div className="flex items-center gap-2">
            <MousePointerClick className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Conteúdo")}</h3>
          </div>

          {/* Botão primário — sempre presente, independente do modelo */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">{translateInterfaceText("Texto")}</Label>
              <Input
                value={actionButton.text || ''}
                onChange={(e) => updateActionButton('text', e.target.value)}
                placeholder={translateInterfaceText("Comprar agora")}
                className="h-[44px]"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">{translateInterfaceText("Tamanho")}</Label>
              <Select
                value={actionButton.size || 'medium'}
                onValueChange={(value) => updateActionButton('size', value)}
              >
                <SelectTrigger className="h-[44px]">
                  <SelectValue placeholder={translateInterfaceText("Selecione o tamanho")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">{translateInterfaceText("Pequeno")}</SelectItem>
                  <SelectItem value="medium">{translateInterfaceText("Médio")}</SelectItem>
                  <SelectItem value="large">{translateInterfaceText("Grande")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">{translateInterfaceText("Link de destino")}</Label>
            <Input
              type="url"
              value={actionButton.url || ''}
              onChange={(e) => updateActionButton('url', e.target.value)}
              onBlur={(e) => {
                const urlWithProtocol = ensureHttps(e.target.value);
                if (urlWithProtocol !== e.target.value) {
                  updateActionButton('url', urlWithProtocol);
                }
              }}
              placeholder="https://exemplo.com"
              className="h-[44px]"
            />
          </div>

          {/* Textos do bloco de oferta, na ordem em que aparecem na tela */}
          {textSlots.length > 0 && (
            <>
              <SectionDivider label={translateInterfaceText("Bloco de oferta")} />

              {textSlots.map((slot) => {
                const element = template[slot] || {};
                const isToggleable = TOGGLEABLE_SLOTS.includes(slot);
                // Ausente conta como ligado: o modelo declarou o slot no layout.
                const isEnabled =
                  !isToggleable ||
                  (element as ActionButtonToggleableText).enabled !== false;

                return (
                  <div key={slot} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">{TEXT_SLOT_LABELS[slot]}</Label>
                      {isToggleable && (
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked) => patchTemplate(slot, { enabled: checked })}
                        />
                      )}
                    </div>

                    {isEnabled && (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px]">
                        <Input
                          value={element.text || ''}
                          onChange={(e) => patchTemplate(slot, { text: e.target.value })}
                          placeholder={TEXT_SLOT_PLACEHOLDERS[slot]}
                          className="h-[44px]"
                        />
                        <Select
                          value={element.size || 'medium'}
                          onValueChange={(value) => patchTemplate(slot, { size: value })}
                        >
                          <SelectTrigger className="h-[44px]">
                            <SelectValue placeholder={translateInterfaceText("Tamanho")} />
                          </SelectTrigger>
                          <SelectContent>
                            {TEXT_SIZE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* Barra de progresso */}
          {showTemplates && hasSlot(model, 'progressBar') && (
            <>
              <SectionDivider label={translateInterfaceText("Barra de progresso")} />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{translateInterfaceText("Exibir barra")}</Label>
                  <Switch
                    checked={progressBar.enabled !== false}
                    onCheckedChange={(checked) =>
                      patchTemplate('progressBar', { enabled: checked })
                    }
                  />
                </div>

                {progressBar.enabled !== false && (
                  <>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">{translateInterfaceText("Preenchimento")}</Label>
                        {/* `??`: 0% é valor válido e não pode virar o default */}
                        <span className="text-sm font-semibold text-primary">
                          {progressBar.percentage ?? 80}%
                        </span>
                      </div>
                      <Slider
                        value={[progressBar.percentage ?? 80]}
                        onValueChange={(values) =>
                          patchTemplate('progressBar', { percentage: values[0] })
                        }
                        min={0}
                        max={100}
                        step={1}
                        className="w-full"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">{translateInterfaceText("Mostrar texto na barra")}</Label>
                      <Switch
                        checked={progressBar.showText !== false}
                        onCheckedChange={(checked) =>
                          patchTemplate('progressBar', { showText: checked })
                        }
                      />
                    </div>

                    {progressBar.showText !== false && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">{translateInterfaceText("Texto da barra")}</Label>
                        <Input
                          value={progressBar.text || ''}
                          onChange={(e) => patchTemplate('progressBar', { text: e.target.value })}
                          placeholder={translateInterfaceText("80% das vagas preenchidas")}
                          className="h-[44px]"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Segundo caminho: downsell ou recusa */}
          {showTemplates && secondaryAs && (
            <>
              <SectionDivider label={secondarySectionLabel(secondaryAs)} />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{translateInterfaceText("Ativar")}</Label>
                  <Switch
                    checked={secondary.enabled !== false}
                    onCheckedChange={(checked) => patchTemplate('secondary', { enabled: checked })}
                  />
                </div>

                {secondary.enabled !== false && (
                  <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px]">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">{translateInterfaceText("Texto")}</Label>
                        <Input
                          value={secondary.text || ''}
                          onChange={(e) => patchTemplate('secondary', { text: e.target.value })}
                          placeholder={translateInterfaceText("Não, obrigado")}
                          className="h-[44px]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">{translateInterfaceText("Forma")}</Label>
                        <Select
                          value={secondaryAs}
                          onValueChange={(value) =>
                            patchTemplate('secondary', { renderAs: value })
                          }
                        >
                          <SelectTrigger className="h-[44px]">
                            <SelectValue placeholder={translateInterfaceText("Forma")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="button">{translateInterfaceText("Botão")}</SelectItem>
                            <SelectItem value="link">{translateInterfaceText("Link discreto")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{translateInterfaceText("Link de destino")}</Label>
                      <Input
                        type="url"
                        value={secondary.url || ''}
                        onChange={(e) => patchTemplate('secondary', { url: e.target.value })}
                        onBlur={(e) => {
                          const urlWithProtocol = ensureHttps(e.target.value);
                          if (urlWithProtocol !== e.target.value) {
                            patchTemplate('secondary', { url: urlWithProtocol });
                          }
                        }}
                        placeholder="https://exemplo.com"
                        className="h-[44px]"
                      />
                    </div>

                    {/* O segundo caminho tem o próprio destino de aba: recusa/
                        downsell normalmente segue na mesma janela. */}
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={secondaryOpenNewTabId}
                        checked={secondary.openInNewTab === true}
                        onCheckedChange={(checked) =>
                          patchTemplate('secondary', { openInNewTab: checked === true })
                        }
                      />
                      <Label
                        htmlFor={secondaryOpenNewTabId}
                        className="cursor-pointer text-sm font-medium"
                      >
                        {translateInterfaceText("Abrir em nova aba")}</Label>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* `!== false`: o botão sempre abriu em nova aba antes do campo
              existir, então ausente continua marcado. */}
          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id={openNewTabId}
              checked={template.openInNewTab !== false}
              onCheckedChange={(checked) => updateTemplate('openInNewTab', checked === true)}
            />
            <Label
              htmlFor={openNewTabId}
              className="cursor-pointer text-sm font-medium"
            >
              {translateInterfaceText("Abrir link do botão em nova aba")}</Label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
